import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import {
    Search,
    MapPin,
    Award,
    Clock,
    MessageCircle,
    User,
    Dumbbell,
    Filter,
    X,
    CheckCircle,
    ChevronDown,
    Loader2,
    Star,
} from 'lucide-react';

interface TrainerProfile {
    id: string;
    user_id: string;
    display_name: string;
    headline: string;
    bio: string;
    workout_styles: string[];
    experience_years: number;
    location_city: string;
    location_state: string;
    preferred_gym: string;
    accepts_online: boolean;
    accepts_in_person: boolean;
    whatsapp_number: string;
    photos: string[];
    certifications: string[];
    is_visible: boolean;
}

const SPECIALTIES = [
    'Musculação',
    'Emagrecimento',
    'Hipertrofia',
    'Funcional',
    'Crossfit',
    'Pilates',
    'Yoga',
    'Corrida',
    'Natação',
    'Artes Marciais',
    'Reabilitação',
    'Gestantes',
    'Idosos',
    'Atletas',
];

const STATES = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
    'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
    'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

export function TrainerSearchPage() {
    const { profile } = useAuth();
    const [searchTerm, setSearchTerm] = useState('');
    const [gymSearch, setGymSearch] = useState('');
    const [selectedState, setSelectedState] = useState('');
    const [selectedCity, setSelectedCity] = useState('');
    const [selectedSpecialty, setSelectedSpecialty] = useState<string | null>(null);
    const [onlineOnly, setOnlineOnly] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [selectedTrainer, setSelectedTrainer] = useState<TrainerProfile | null>(null);

    // Fetch trainers from tinderfit_profiles with is_visible = true
    const { data: trainers, isLoading } = useQuery({
        queryKey: ['trainer-search', selectedState, selectedCity, selectedSpecialty, onlineOnly, gymSearch],
        queryFn: async () => {
            let query = supabase
                .from('tinderfit_profiles')
                .select(`
                    id,
                    user_id,
                    display_name,
                    headline,
                    bio,
                    workout_styles,
                    experience_years,
                    location_city,
                    location_state,
                    preferred_gym,
                    accepts_online,
                    accepts_in_person,
                    whatsapp_number,
                    photos,
                    certifications,
                    is_visible
                `)
                .eq('is_visible', true)
                .not('whatsapp_number', 'is', null);

            if (selectedState) {
                query = query.eq('location_state', selectedState);
            }
            if (selectedCity) {
                query = query.ilike('location_city', `%${selectedCity}%`);
            }
            if (selectedSpecialty) {
                query = query.contains('workout_styles', [selectedSpecialty]);
            }
            if (onlineOnly) {
                query = query.eq('accepts_online', true);
            }
            if (gymSearch) {
                query = query.ilike('preferred_gym', `%${gymSearch}%`);
            }

            const { data, error } = await query.order('experience_years', { ascending: false });

            if (error) throw error;
            return data as TrainerProfile[];
        },
    });

    const filteredTrainers = trainers?.filter(trainer => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (
            trainer.display_name?.toLowerCase().includes(search) ||
            trainer.headline?.toLowerCase().includes(search) ||
            trainer.bio?.toLowerCase().includes(search) ||
            trainer.preferred_gym?.toLowerCase().includes(search)
        );
    });

    const handleWhatsAppContact = (trainer: TrainerProfile) => {
        const phone = trainer.whatsapp_number?.replace(/\D/g, '');
        if (!phone) return;

        const message = encodeURIComponent(
            `Olá ${trainer.display_name}! 👋\n\n` +
            `Vi seu perfil na plataforma *GoFit* e gostaria de saber mais sobre seus serviços de Personal Trainer.\n\n` +
            `Pode me passar mais informações sobre valores e disponibilidade?\n\n` +
            `Obrigado(a)!`
        );

        window.open(`https://wa.me/55${phone}?text=${message}`, '_blank');
    };

    const clearFilters = () => {
        setSelectedState('');
        setSelectedCity('');
        setSelectedSpecialty(null);
        setOnlineOnly(false);
        setGymSearch('');
        setSearchTerm('');
    };

    const hasFilters = selectedState || selectedCity || selectedSpecialty || onlineOnly || gymSearch;

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div>
                <h1 className="section-title flex items-center gap-2">
                    <Dumbbell className="w-6 h-6" />
                    Encontrar Personal Trainer
                </h1>
                <p className="section-subtitle">
                    Busque profissionais próximos à sua academia e entre em contato diretamente
                </p>
            </div>

            {/* Search Bar */}
            <div className="card space-y-4">
                <div className="flex gap-3">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Buscar por nome, especialidade..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="input pl-10"
                        />
                    </div>
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={`btn-secondary flex items-center gap-2 ${showFilters ? 'bg-primary-100 dark:bg-primary-900/30' : ''}`}
                    >
                        <Filter className="w-4 h-4" />
                        Filtros
                        {hasFilters && (
                            <span className="w-2 h-2 rounded-full bg-primary-500" />
                        )}
                    </button>
                </div>

                {/* Filters Panel */}
                {showFilters && (
                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
                        {/* Gym Search */}
                        <div>
                            <label className="label flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-primary-600" />
                                Buscar por Academia
                            </label>
                            <input
                                type="text"
                                placeholder="Ex: Smart Fit, Bluefit, Academia do Bairro..."
                                value={gymSearch}
                                onChange={(e) => setGymSearch(e.target.value)}
                                className="input"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Encontre personais que atendem na sua academia
                            </p>
                        </div>

                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {/* State Filter */}
                            <div>
                                <label className="label">Estado</label>
                                <select
                                    value={selectedState}
                                    onChange={(e) => setSelectedState(e.target.value)}
                                    className="input"
                                >
                                    <option value="">Todos</option>
                                    {STATES.map(state => (
                                        <option key={state} value={state}>{state}</option>
                                    ))}
                                </select>
                            </div>

                            {/* City Filter */}
                            <div>
                                <label className="label">Cidade</label>
                                <input
                                    type="text"
                                    placeholder="Digite a cidade..."
                                    value={selectedCity}
                                    onChange={(e) => setSelectedCity(e.target.value)}
                                    className="input"
                                />
                            </div>

                            {/* Specialty Filter */}
                            <div>
                                <label className="label">Especialidade</label>
                                <select
                                    value={selectedSpecialty || ''}
                                    onChange={(e) => setSelectedSpecialty(e.target.value || null)}
                                    className="input"
                                >
                                    <option value="">Todas</option>
                                    {SPECIALTIES.map(spec => (
                                        <option key={spec} value={spec}>{spec}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Online Only Toggle */}
                            <div className="flex items-end">
                                <label className="flex items-center gap-2 cursor-pointer h-10">
                                    <input
                                        type="checkbox"
                                        checked={onlineOnly}
                                        onChange={(e) => setOnlineOnly(e.target.checked)}
                                        className="w-4 h-4 rounded text-primary-500"
                                    />
                                    <span className="text-gray-700 dark:text-gray-300">Apenas Online</span>
                                </label>
                            </div>
                        </div>

                        {hasFilters && (
                            <button
                                onClick={clearFilters}
                                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                            >
                                <X className="w-4 h-4" />
                                Limpar filtros
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Results */}
            {isLoading ? (
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                </div>
            ) : filteredTrainers && filteredTrainers.length > 0 ? (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {filteredTrainers.map((trainer) => (
                        <div
                            key={trainer.id}
                            className="card-hover group"
                        >
                            {/* Header */}
                            <div className="flex items-start gap-4">
                                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center flex-shrink-0 overflow-hidden">
                                    {trainer.photos?.[0] ? (
                                        <img
                                            src={trainer.photos[0]}
                                            alt={trainer.display_name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <User className="w-8 h-8 text-white" />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                                        {trainer.display_name}
                                    </h3>
                                    {trainer.headline && (
                                        <p className="text-sm text-primary-600 dark:text-primary-400 truncate">
                                            {trainer.headline}
                                        </p>
                                    )}
                                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                                        {trainer.location_city && (
                                            <span className="flex items-center gap-1">
                                                <MapPin className="w-3 h-3" />
                                                {trainer.location_city}{trainer.location_state && `, ${trainer.location_state}`}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Gym */}
                            {trainer.preferred_gym && (
                                <div className="mt-3 p-2 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                                    <p className="text-sm text-primary-700 dark:text-primary-300 flex items-center gap-2">
                                        <Dumbbell className="w-4 h-4" />
                                        Atende em: <strong>{trainer.preferred_gym}</strong>
                                    </p>
                                </div>
                            )}

                            {/* Experience */}
                            {trainer.experience_years > 0 && (
                                <div className="mt-3 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                    <Clock className="w-4 h-4" />
                                    {trainer.experience_years} anos de experiência
                                </div>
                            )}

                            {/* Specialties */}
                            {trainer.workout_styles && trainer.workout_styles.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-1">
                                    {trainer.workout_styles.slice(0, 4).map((style) => (
                                        <span
                                            key={style}
                                            className="px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-xs rounded-full"
                                        >
                                            {style}
                                        </span>
                                    ))}
                                    {trainer.workout_styles.length > 4 && (
                                        <span className="px-2 py-0.5 text-gray-400 text-xs">
                                            +{trainer.workout_styles.length - 4}
                                        </span>
                                    )}
                                </div>
                            )}

                            {/* Modalities */}
                            <div className="mt-3 flex flex-wrap gap-2">
                                {trainer.accepts_in_person && (
                                    <span className="flex items-center gap-1 text-xs text-green-600">
                                        <CheckCircle className="w-3 h-3" />
                                        Presencial
                                    </span>
                                )}
                                {trainer.accepts_online && (
                                    <span className="flex items-center gap-1 text-xs text-blue-600">
                                        <CheckCircle className="w-3 h-3" />
                                        Online
                                    </span>
                                )}
                            </div>

                            {/* CTA */}
                            <button
                                onClick={() => handleWhatsAppContact(trainer)}
                                className="mt-4 w-full py-3 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg flex items-center justify-center gap-2 transition-colors"
                            >
                                <MessageCircle className="w-5 h-5" />
                                Pedir Orçamento
                            </button>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="card text-center py-16">
                    <Dumbbell className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                        Nenhum Personal encontrado
                    </h3>
                    <p className="text-gray-500 max-w-md mx-auto">
                        {hasFilters
                            ? 'Tente ajustar os filtros para encontrar mais profissionais.'
                            : 'Ainda não há profissionais cadastrados na plataforma.'}
                    </p>
                    {hasFilters && (
                        <button
                            onClick={clearFilters}
                            className="mt-4 btn-secondary"
                        >
                            Limpar filtros
                        </button>
                    )}
                </div>
            )}

            {/* Results count */}
            {filteredTrainers && filteredTrainers.length > 0 && (
                <p className="text-sm text-gray-500 text-center">
                    {filteredTrainers.length} {filteredTrainers.length === 1 ? 'personal encontrado' : 'personais encontrados'}
                </p>
            )}
        </div>
    );
}
